import { forwardRef, useEffect, useId, useRef, useState } from "react";
import type {
  ChangeEvent,
  InputHTMLAttributes,
  Ref,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";
import { classNames } from "../internal/classNames";
import "./Field.css";

interface FieldBaseProps {
  id?: string;
  label: string;
  description?: string;
  error?: string;
  success?: string;
  required?: boolean;
  compact?: boolean;
  leadingAdornment?: ReactNode;
  trailingAdornment?: ReactNode;
  format?: "phone";
  className?: string;
}

type InputFieldProps = FieldBaseProps &
  Omit<InputHTMLAttributes<HTMLInputElement>, keyof FieldBaseProps | "children"> & {
    control?: "input";
  };

type TextareaFieldProps = FieldBaseProps &
  Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, keyof FieldBaseProps> & {
    control: "textarea";
  };

type SelectFieldProps = FieldBaseProps &
  Omit<SelectHTMLAttributes<HTMLSelectElement>, keyof FieldBaseProps> & {
    control: "select";
    children: ReactNode;
  };

export type FieldProps = InputFieldProps | TextareaFieldProps | SelectFieldProps;
export type FieldElement = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

export function formatPhoneNumber(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
}

function SelectChevron() {
  return (
    <svg
      aria-hidden="true"
      className="cp-field__select-icon"
      fill="none"
      focusable="false"
      viewBox="0 0 16 16"
    >
      <path
        d="m4 6 4 4 4-4"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
    </svg>
  );
}

function PasswordVisibilityIcon({ visible }: { visible: boolean }) {
  return (
    <svg
      aria-hidden="true"
      className="cp-field__password-icon"
      fill="none"
      focusable="false"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    >
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" />
      <circle cx="12" cy="12" r="3" />
      {visible && <path d="m3 3 18 18" />}
    </svg>
  );
}

export const Field = forwardRef<FieldElement, FieldProps>(function Field(
  {
    id: providedId,
    label,
    description,
    error,
    success,
    required = false,
    compact = false,
    leadingAdornment,
    trailingAdornment,
    format,
    className,
    control = "input",
    ...controlProps
  },
  ref,
) {
  const generatedId = useId();
  const shellRef = useRef<HTMLDivElement>(null);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const id = providedId ?? `cp-field-${generatedId}`;
  const descriptionId = description ? `${id}-description` : undefined;
  const feedbackId = error || success ? `${id}-feedback` : undefined;
  const describedBy = [descriptionId, feedbackId, controlProps["aria-describedby"]]
    .filter(Boolean)
    .join(" ") || undefined;
  const state = error ? "error" : success ? "success" : "default";
  const commonProps = {
    ...controlProps,
    id,
    required,
    "aria-describedby": describedBy,
    "aria-invalid": error ? true : undefined,
    className: "cp-field__control",
  };
  const rawInputProps = commonProps as InputHTMLAttributes<HTMLInputElement>;
  const isPassword = control === "input" && rawInputProps.type === "password";
  const passwordDisabled = rawInputProps.disabled;

  useEffect(() => {
    if (!isPassword || passwordDisabled) {
      setPasswordVisible(false);
      return;
    }
    if (!passwordVisible) return;

    const hidePassword = () => setPasswordVisible(false);
    const hideWhenBackgrounded = () => {
      if (document.hidden) hidePassword();
    };
    const form = shellRef.current?.querySelector("input")?.form;
    form?.addEventListener("submit", hidePassword);
    form?.addEventListener("reset", hidePassword);
    window.addEventListener("blur", hidePassword);
    document.addEventListener("visibilitychange", hideWhenBackgrounded);
    return () => {
      form?.removeEventListener("submit", hidePassword);
      form?.removeEventListener("reset", hidePassword);
      window.removeEventListener("blur", hidePassword);
      document.removeEventListener("visibilitychange", hideWhenBackgrounded);
    };
  }, [isPassword, passwordDisabled, passwordVisible]);

  const formatsPhone = control === "input" && rawInputProps.type === "tel" && format === "phone";
  const inputProps = formatsPhone ? {
    ...rawInputProps,
    defaultValue: typeof rawInputProps.defaultValue === "string"
      || typeof rawInputProps.defaultValue === "number"
      ? formatPhoneNumber(String(rawInputProps.defaultValue))
      : rawInputProps.defaultValue,
    value: typeof rawInputProps.value === "string" || typeof rawInputProps.value === "number"
      ? formatPhoneNumber(String(rawInputProps.value))
      : rawInputProps.value,
    inputMode: rawInputProps.inputMode ?? "numeric",
    maxLength: rawInputProps.maxLength ?? 13,
    placeholder: rawInputProps.placeholder ?? "010-0000-0000",
    onChange: (event: ChangeEvent<HTMLInputElement>) => {
      event.currentTarget.value = formatPhoneNumber(event.currentTarget.value);
      rawInputProps.onChange?.(event);
    },
  } : rawInputProps;

  return (
    <div className={classNames("cp-field", compact && "cp-field--compact", className)} data-state={state}>
      <label className="cp-field__label" htmlFor={id}>
        {label}
        {required && <span aria-hidden="true" className="cp-field__required">필수</span>}
      </label>
      {description && <span className="cp-field__description" id={descriptionId}>{description}</span>}
      <div
        className="cp-field__shell"
        ref={shellRef}
        onBlur={isPassword ? (event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) setPasswordVisible(false);
        } : undefined}
      >
        {leadingAdornment && <span aria-hidden="true" className="cp-field__adornment">{leadingAdornment}</span>}
        {control === "textarea" ? (
          <textarea {...(commonProps as TextareaHTMLAttributes<HTMLTextAreaElement>)} ref={ref as Ref<HTMLTextAreaElement>} />
        ) : control === "select" ? (
          <select {...(commonProps as SelectHTMLAttributes<HTMLSelectElement>)} ref={ref as Ref<HTMLSelectElement>}>
            {(controlProps as SelectFieldProps).children}
          </select>
        ) : (
          <input
            {...inputProps}
            ref={ref as Ref<HTMLInputElement>}
            type={isPassword && passwordVisible && !passwordDisabled ? "text" : inputProps.type}
          />
        )}
        {control === "select" && !trailingAdornment && (
          <span aria-hidden="true" className="cp-field__select-indicator">
            <SelectChevron />
          </span>
        )}
        {trailingAdornment && <span aria-hidden="true" className="cp-field__adornment">{trailingAdornment}</span>}
        {isPassword && (
          <button
            aria-controls={id}
            aria-label={`${label} ${passwordVisible ? "숨기기" : "보기"}`}
            className="cp-field__password-toggle"
            disabled={passwordDisabled}
            onClick={() => setPasswordVisible((visible) => !visible)}
            onMouseDown={(event) => event.preventDefault()}
            type="button"
          >
            <PasswordVisibilityIcon visible={passwordVisible} />
          </button>
        )}
      </div>
      {(error || success) && (
        <span
          className="cp-field__feedback"
          id={feedbackId}
          role={error ? "alert" : "status"}
        >
          {error ?? success}
        </span>
      )}
    </div>
  );
});
